import { useEffect, useState } from 'react'
import { API_BASE } from './api'
import { communityIds } from './events'

/* ================================================================== */
/*  Public "community circles" feed from the myNext50 backend.         */
/*                                                                     */
/*  A circle is a themed sub-community within a community (e.g. the     */
/*  "Astrology Circle" inside Cosmos). The endpoint is public/no-auth,  */
/*  so — like the events + lifecrafters feeds — we do NOT send          */
/*  credentials, resolve the parent community id once, and fall back to */
/*  the hand-authored static content when the feed is empty or          */
/*  unreachable. Dev goes through the Vite proxy; prod needs the         */
/*  LifeCraft origin allowlisted in the backend CORS config.            */
/* ================================================================== */

/** Raw circle from `GET /community-circles/public` (only fields we render). */
type RawCircle = {
  _id: string
  name?: string
  description?: string
  short_description?: string
  banner?: { file_url?: string; alt_text?: string }
}

/** Normalised circle for the UI. */
export type DisplayCircle = {
  id: string
  name: string
  blurb: string
  image?: string
  alt?: string
}

async function getPublic<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`)
  if (!res.ok) throw new Error(`circles request failed (${res.status})`)
  return res.json() as Promise<T>
}

function mapCircle(c: RawCircle): DisplayCircle {
  const blurb = (c.short_description || c.description || '').replace(/[*_#`]/g, '').trim()
  return {
    id: c._id,
    name: c.name?.trim() || 'Circle',
    blurb,
    image: c.banner?.file_url || undefined,
    alt: c.banner?.alt_text || undefined,
  }
}

/** Circles within one community (matched by display name). */
export async function fetchCommunityCircles(communityName: string, limit = 12): Promise<DisplayCircle[]> {
  const id = (await communityIds())[communityName.trim().toUpperCase()]
  if (!id) return []
  const data = await getPublic<{ results?: RawCircle[] }>(
    `/community-circles/public?communityId=${id}&limit=${limit}&sortBy=createdAt:asc`,
  )
  return (data.results ?? []).map(mapCircle)
}

/** null while loading (keep static), [] when none. */
export function useCommunityCircles(communityName: string | undefined, limit = 12): DisplayCircle[] | null {
  const [circles, setCircles] = useState<DisplayCircle[] | null>(null)
  useEffect(() => {
    if (!communityName) {
      setCircles([])
      return
    }
    let alive = true
    setCircles(null)
    fetchCommunityCircles(communityName, limit)
      .then((r) => alive && setCircles(r))
      .catch(() => alive && setCircles([]))
    return () => {
      alive = false
    }
  }, [communityName, limit])
  return circles
}
